(() => {
  const API_VERSION = '2026-04';

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const parseTextList = (value) => {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(item => String(item).trim()).filter(Boolean);
      }
      if (typeof parsed === 'string') {
        return [parsed.trim()].filter(Boolean);
      }
    } catch (error) {
      // Fallback for any legacy/plain-text values.
    }

    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  };

  const init = (root) => {
    if (!root || root.dataset.tllReady === 'true') return;
    root.dataset.tllReady = 'true';

    const shopDomain = root.dataset.shopDomain;
    const token = root.dataset.storefrontToken;
    const endpoint = `https://${shopDomain}/api/${API_VERSION}/graphql.json`;

    const productInput = root.querySelector('.tll-product-search');
    const resultsBox = root.querySelector('.tll-search-dropdown');
    const lotField = root.querySelector('[data-lot-field]');
    const lotInput = root.querySelector('.tll-lot-number');
    const searchButton = root.querySelector('[data-search-button]');
    const result = root.querySelector('[data-result]');
    const matchResult = root.querySelector('[data-match-result]');
    const selectedBox = root.querySelector('[data-selected-product]');
    const selectedTitle = root.querySelector('[data-selected-product-title]');
    const selectedImageWrap = root.querySelector('[data-selected-product-image-wrap]');
    const selectedImage = root.querySelector('[data-selected-product-image]');
    const selectedProductLink = root.querySelector('[data-selected-product-link]');
    const changeProduct = root.querySelector('[data-change-product]');
    const historyList = root.querySelector('[data-history-list]');
    const historyEmpty = root.querySelector('[data-history-empty]');
    const historyClear = root.querySelector('[data-history-clear]');
    let selectedProductId = null;
    let selectedProductTitle = '';
    let selectedProductImage = '';
    let selectedProductImageAlt = '';
    let selectedProductUrl = '';
    let selectedProductHandle = '';
    let selectedProductTagLine = '';
    let currentSubscription = null;
    let debounceTimer = null;
    const historyStorageKey = 'nutrachamps_coa_history_v1';
    const historyDays = Math.max(7, Number(root.dataset.historyDays) || 60);
    let transientHistory = [];

    const storageAvailable = () => {
      try {
        const testKey = `${historyStorageKey}_available`;
        window.localStorage.setItem(testKey, '1');
        window.localStorage.removeItem(testKey);
        return true;
      } catch (error) {
        return false;
      }
    };

    const readHistory = () => {
      const cutoff = Date.now() - (historyDays * 24 * 60 * 60 * 1000);

      if (!storageAvailable()) {
        transientHistory = transientHistory.filter(item => Number(item?.savedAt) >= cutoff);
        return transientHistory;
      }

      try {
        const parsed = JSON.parse(window.localStorage.getItem(historyStorageKey) || '[]');
        const history = Array.isArray(parsed)
          ? parsed.filter(item => Number(item?.savedAt) >= cutoff)
          : [];

        window.localStorage.setItem(historyStorageKey, JSON.stringify(history));
        return history;
      } catch (error) {
        return [];
      }
    };

    const renderHistory = () => {
      const history = readHistory();

      if (historyClear) historyClear.hidden = history.length === 0;
      if (historyEmpty) historyEmpty.hidden = history.length > 0;

      if (historyList) {
        historyList.innerHTML = history.map(item => `
          <article class="tll-history-item">
            <time datetime="${escapeHtml(item.savedIso || '')}">${escapeHtml(item.displayDate || '')}</time>
            <span class="tll-history-item__product">${escapeHtml(item.productTitle || '')}</span>
            <span class="tll-history-item__lot">Lot ${escapeHtml(item.lotNumber || '')}</span>
            <strong>COA</strong>
            <button type="button" data-history-open="${escapeHtml(item.coaUrl || '')}">
              Open PDF <span aria-hidden="true">→</span>
            </button>
          </article>
        `).join('');
      }
    };

    const saveHistoryEntry = (entry) => {
      if (!entry?.coaUrl) return;

      const history = readHistory().filter(item => (
        item.coaUrl !== entry.coaUrl || item.lotNumber !== entry.lotNumber
      ));

      history.unshift(entry);
      transientHistory = history.slice(0, 10);

      if (storageAvailable()) {
        window.localStorage.setItem(historyStorageKey, JSON.stringify(transientHistory));
      }

      renderHistory();
    };

    const clearHistory = () => {
      transientHistory = [];
      if (storageAvailable()) window.localStorage.removeItem(historyStorageKey);
      renderHistory();
    };

    const closePdfModal = () => {
      const modal = document.querySelector('[data-tll-pdf-modal]');
      if (!modal) return;

      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('tll-modal-open');

      const frame = modal.querySelector('[data-tll-pdf-frame]');
      if (frame) frame.removeAttribute('src');
    };

    const openPdfModal = (url, title = 'Certificate of Analysis') => {
      let modal = document.querySelector('[data-tll-pdf-modal]');

      if (!modal) {
        modal = document.createElement('div');
        modal.className = 'tll-pdf-modal';
        modal.setAttribute('data-tll-pdf-modal', '');
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML = `
          <div class="tll-pdf-modal__backdrop" data-tll-pdf-close></div>
          <div class="tll-pdf-modal__dialog" role="dialog" aria-modal="true" aria-label="Certificate of Analysis">
            <div class="tll-pdf-modal__header">
              <strong data-tll-pdf-title>Certificate of Analysis</strong>
              <div class="tll-pdf-modal__actions">
                <a class="tll-pdf-modal__external" data-tll-pdf-external href="#" target="_blank" rel="noopener">Open in new tab</a>
                <button type="button" class="tll-pdf-modal__close" data-tll-pdf-close aria-label="Close PDF">&times;</button>
              </div>
            </div>
            <iframe class="tll-pdf-modal__frame" data-tll-pdf-frame title="Certificate of Analysis"></iframe>
          </div>
        `;
        document.body.appendChild(modal);
      }

      const frame = modal.querySelector('[data-tll-pdf-frame]');
      const titleEl = modal.querySelector('[data-tll-pdf-title]');
      const externalLink = modal.querySelector('[data-tll-pdf-external]');
      if (titleEl) titleEl.textContent = title;
      if (externalLink) externalLink.href = url;
      if (frame) frame.src = `${url}#page=1&zoom=page-width`;

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tll-modal-open');
    };

    const graphql = async (query, variables = {}, signal = undefined) => {
      if (!token) throw new Error('Missing Storefront API token.');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables }),
        signal
      });

      const payload = await response.json();

      if (!response.ok || payload.errors) {
        throw new Error(
          payload.errors?.map(error => error.message).join('; ')
          || `Shopify API request failed (${response.status}).`
        );
      }

      return payload.data;
    };

    const setLoading = (loading) => {
      searchButton.disabled = loading;
      searchButton.classList.toggle('is-loading', loading);
      searchButton.setAttribute('aria-busy', String(loading));
    };

    const setMessage = (message, type = 'info') => {
      result.innerHTML = `<div class="tll-message tll-message--${type}">${escapeHtml(message)}</div>`;
    };

    const expandResult = (element, delay = 0) => {
      if (!element?.firstElementChild) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const targetHeight = element.scrollHeight;
      element.style.overflow = 'hidden';

      const animation = element.animate(
        [
          { maxHeight: '0px', opacity: 0, transform: 'translateY(-8px)' },
          { maxHeight: `${targetHeight}px`, opacity: 1, transform: 'translateY(0)' }
        ],
        {
          duration: 520,
          delay,
          easing: 'cubic-bezier(.22, 1, .36, 1)',
          fill: 'both'
        }
      );

      animation.addEventListener('finish', () => {
        animation.cancel();
        element.style.removeProperty('overflow');
      }, { once: true });
    };

    const closeResults = () => {
      resultsBox.innerHTML = '';
      resultsBox.classList.remove('is-active');
    };

    const updateSelectedProductImage = (url, alt) => {
      if (url) {
        selectedImage.src = url;
        selectedImage.alt = alt || selectedProductTitle || 'Selected product';
        selectedImageWrap.hidden = false;
      } else {
        selectedImage.removeAttribute('src');
        selectedImage.alt = '';
        selectedImageWrap.hidden = true;
      }
    };

    const selectProduct = (id, title, imageUrl = '', imageAlt = '', productUrl = '', productHandle = '', productTagLine = '') => {
      selectedProductId = id.replace('gid://shopify/Product/', '');
      selectedProductTitle = title;
      selectedProductImage = imageUrl;
      selectedProductImageAlt = imageAlt;
      selectedProductUrl = productUrl;
      selectedProductHandle = productHandle || productUrl.split('/products/')[1]?.split(/[?#]/)[0] || '';
      selectedProductTagLine = productTagLine || '';

      productInput.value = title;
      closeResults();

      productInput.closest('.tll-field--product').hidden = true;
      selectedTitle.textContent = title;
      updateSelectedProductImage(imageUrl, imageAlt);

      if (selectedProductLink) {
        if (productUrl) {
          selectedProductLink.href = productUrl;
          selectedProductLink.hidden = false;
        } else {
          selectedProductLink.href = '#';
          selectedProductLink.hidden = true;
        }
      }

      selectedBox.hidden = false;
      lotField.hidden = false;
      matchResult.innerHTML = '';
      result.innerHTML = '';
      lotInput.focus();
    };

    let searchController = null;

    const searchProducts = async () => {
      const term = productInput.value.trim();

      if (!term) {
        closeResults();
        return;
      }

      if (searchController) searchController.abort();
      searchController = new AbortController();

      const query = `
        query SearchProducts($query: String!) {
          products(first: 20, query: $query) {
            edges {
              node {
                id
                title
                handle
                productTagLine: metafield(namespace: "custom", key: "product_tag_line") {
                  value
                }
                featuredImage {
                  url(transform: { maxWidth: 120, maxHeight: 120 })
                  altText
                }
              }
            }
          }
        }
      `;

      try {
        const data = await graphql(
          query,
          { query: `title:${term}*` },
          searchController.signal
        );

        const products = data?.products?.edges || [];

        if (!products.length) {
          resultsBox.innerHTML = '<div class="tll-search-empty">No products found.</div>';
          resultsBox.classList.add('is-active');
          return;
        }

        resultsBox.innerHTML = products.map(({ node }) => {
          const imageUrl = node.featuredImage?.url || '';
          const imageAlt = node.featuredImage?.altText || node.title;
          const productUrl = `/products/${encodeURIComponent(node.handle)}`;
          const imageMarkup = imageUrl
            ? `<img class="tll-search-item__image" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" loading="lazy">`
            : '<span class="tll-search-item__image tll-search-item__image--placeholder" aria-hidden="true"></span>';

          return `
            <button
              type="button"
              class="tll-search-item"
              role="option"
              data-product-id="${escapeHtml(node.id)}"
              data-product-title="${escapeHtml(node.title)}"
              data-product-image="${escapeHtml(imageUrl)}"
              data-product-image-alt="${escapeHtml(imageAlt)}"
              data-product-url="${escapeHtml(productUrl)}"
              data-product-handle="${escapeHtml(node.handle)}"
              data-product-tagline="${escapeHtml(node.productTagLine?.value || '')}"
            >
              <span class="tll-search-item__main">
                ${imageMarkup}
                <span class="tll-search-item__title">${escapeHtml(node.title)}</span>
              </span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          `;
        }).join('');

        resultsBox.classList.add('is-active');
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Track Your Lot] Product search error:', error);
        resultsBox.innerHTML = '<div class="tll-search-empty">Unable to search products right now.</div>';
        resultsBox.classList.add('is-active');
      }
    };

    const fetchProduct = async (productId) => {
      const query = `
        query ProductCOAData($id: ID!) {
          product(id: $id) {
            id
            title
            handle
            featuredImage {
              url(transform: { maxWidth: 600, maxHeight: 600 })
              altText
            }
            productTagLine: metafield(namespace: "custom", key: "product_tag_line") {
              value
            }
            metafield(namespace: "custom", key: "coa_records") {
              references(first: 100) {
                nodes {
                  ... on Metaobject {
                    id
                    handle
                    fields {
                      key
                      type
                      value
                      reference {
                        ... on GenericFile {
                          url
                          mimeType
                          previewImage {
                            url
                          }
                        }
                        ... on MediaImage {
                          image {
                            url
                            altText
                            width
                            height
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const data = await graphql(query, {
        id: `gid://shopify/Product/${productId}`
      });

      return data?.product;
    };

    const fetchProductTagLine = async (handle) => {
      if (!handle) return '';

      const query = `
        query ProductTagLine($handle: String!) {
          product(handle: $handle) {
            metafields(identifiers: [{ namespace: "custom", key: "product_tag_line" }]) {
              value
            }
          }
        }
      `;

      try {
        const data = await graphql(query, { handle });
        return data?.product?.metafields?.[0]?.value
          ? String(data.product.metafields[0].value).trim()
          : '';
      } catch (error) {
        console.warn('[Track Your Lot] Product tagline fallback unavailable:', error);
        return '';
      }
    };

    const getField = (record, key) => {
      return (record?.fields || []).find(field => field?.key === key) || null;
    };

    const getRecordLots = (record) => {
      return parseTextList(getField(record, 'lot_numbers')?.value)
        .map(lot => lot.toLowerCase());
    };

    const getRecordFile = (record) => {
      return getField(record, 'coa_file')?.reference || null;
    };

    const getOptionalText = (record, key) => {
      const value = getField(record, key)?.value;
      return value ? String(value).trim() : '';
    };

    const formatDate = (value) => {
      if (!value) return '';

      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return value;

      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    };

    const fetchDefaultSubscription = async () => {
      if (!selectedProductHandle) return null;

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(
          `${rootPath}products/${encodeURIComponent(selectedProductHandle)}.js`,
          { headers: { 'Accept': 'application/json' } }
        );

        if (!response.ok) throw new Error(`Product Ajax request failed (${response.status})`);

        const product = await response.json();
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const variant = variants.find(v => v.available) || variants[0];
        if (!variant) return null;

        const allocations = Array.isArray(variant.selling_plan_allocations)
          ? variant.selling_plan_allocations
          : [];

        if (!allocations.length) return null;

        const plansById = new Map();
        (product?.selling_plan_groups || []).forEach(group => {
          (group?.selling_plans || []).forEach(plan => {
            plansById.set(Number(plan.id), plan);
          });
        });

        const planOptions = allocations.map(allocation => {
          const plan = plansById.get(Number(allocation.selling_plan_id)) || null;
          const optionValue = plan?.options?.[0]?.value || plan?.name || '';
          const cadenceMatch = String(optionValue).match(/(\d+)\s*days?/i);
          const supplyLabel = cadenceMatch ? `${cadenceMatch[1]}-Day Supply` : '';

          const regularPrice = Number(allocation.compare_at_price ?? variant.price);
          const subPrice = Number(allocation.price);
          const savingsAmount = Number.isFinite(regularPrice) && Number.isFinite(subPrice)
            ? Math.max(0, regularPrice - subPrice)
            : null;

          let percent = null;
          let recurringPercent = null;
          const adjustments = Array.isArray(plan?.price_adjustments) ? plan.price_adjustments : [];
          const firstAdjustment = adjustments[0];
          const recurringAdjustment = adjustments.length > 1
            ? adjustments[adjustments.length - 1]
            : firstAdjustment;

          if (firstAdjustment?.value_type === 'percentage' && Number.isFinite(Number(firstAdjustment.value))) {
            percent = Number(firstAdjustment.value);
          } else if (Number.isFinite(regularPrice) && regularPrice > 0 && Number.isFinite(subPrice)) {
            percent = Math.round((1 - subPrice / regularPrice) * 100);
          }

          if (
            recurringAdjustment?.value_type === 'percentage'
            && Number.isFinite(Number(recurringAdjustment.value))
          ) {
            recurringPercent = Number(recurringAdjustment.value);
          } else {
            recurringPercent = percent;
          }

          return {
            sellingPlanId: Number(allocation.selling_plan_id),
            label: optionValue,
            supplyLabel,
            percent: Number.isFinite(percent) ? Math.max(0, percent) : null,
            recurringPercent: Number.isFinite(recurringPercent) ? Math.max(0, recurringPercent) : null,
            regularPrice: Number.isFinite(regularPrice) ? regularPrice : null,
            subPrice: Number.isFinite(subPrice) ? subPrice : null,
            savingsAmount: Number.isFinite(savingsAmount) ? savingsAmount : null
          };
        });

        const firstPlan = planOptions[0];
        if (!firstPlan) return null;

        return {
          variantId: Number(variant.id),
          sellingPlanId: firstPlan.sellingPlanId,
          percent: firstPlan.percent,
          recurringPercent: firstPlan.recurringPercent,
          regularPrice: firstPlan.regularPrice,
          subPrice: firstPlan.subPrice,
          savingsAmount: firstPlan.savingsAmount,
          supplyLabel: firstPlan.supplyLabel,
          label: firstPlan.label,
          plans: planOptions
        };
      } catch (error) {
        console.warn('[Track Your Lot] Subscription data unavailable:', error);
        return null;
      }
    };

    const formatMoney = (cents) => {
      if (!Number.isFinite(Number(cents))) return '';

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD'
        }).format(Number(cents) / 100);
      } catch (error) {
        return `$${(Number(cents) / 100).toFixed(2)}`;
      }
    };


    const applySubscriptionPlan = (sellingPlanId) => {
      if (!currentSubscription?.plans?.length) return;

      const selected = currentSubscription.plans.find(
        plan => Number(plan.sellingPlanId) === Number(sellingPlanId)
      );

      if (!selected) return;

      currentSubscription.sellingPlanId = selected.sellingPlanId;
      currentSubscription.percent = selected.percent;
      currentSubscription.recurringPercent = selected.recurringPercent;
      currentSubscription.regularPrice = selected.regularPrice;
      currentSubscription.subPrice = selected.subPrice;
      currentSubscription.savingsAmount = selected.savingsAmount;
      currentSubscription.supplyLabel = selected.supplyLabel;
      currentSubscription.label = selected.label;
    };


    const syncAndOpenRebuyCart = async () => {
      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}cart.js`, {
          headers: { 'Accept': 'application/json' }
        });
        const cart = await response.json();

        if (window.Rebuy?.SmartCart?.setCart) window.Rebuy.SmartCart.setCart(cart);
        if (window.Rebuy?.SmartCart?.show) window.Rebuy.SmartCart.show();
      } catch (error) {
        console.warn('[Track Your Lot] Could not sync/open Rebuy cart:', error);
      }
    };

    const addDefaultSubscriptionToCart = async (button) => {
      if (!currentSubscription) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: currentSubscription.variantId,
              quantity: 1,
              selling_plan: currentSubscription.sellingPlanId
            }]
          })
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Unable to add subscription');
        }

        button.textContent = 'Added ✓';
        await syncAndOpenRebuyCart();

        setTimeout(() => {
          if (document.body.contains(button)) {
            button.disabled = false;
            button.textContent = originalText;
          }
        }, 1500);
      } catch (error) {
        console.error('[Track Your Lot] Add subscription error:', error);
        button.disabled = false;
        button.textContent = originalText;
        setMessage('There was an error adding the subscription. Please try again.', 'error');
      }
    };

        const ensureSupplementFactsModal = () => {
      let modal = document.querySelector('[data-tll-supplement-modal]');

      if (modal) return modal;

      modal = document.createElement('div');
      modal.className = 'tll-supplement-modal';
      modal.setAttribute('data-tll-supplement-modal', '');
      modal.setAttribute('hidden', '');
      modal.innerHTML = `
        <div class="tll-supplement-modal__backdrop" data-tll-supplement-close></div>
        <div class="tll-supplement-modal__dialog" role="dialog" aria-modal="true" aria-label="Supplement Facts">
          <button
            type="button"
            class="tll-supplement-modal__close"
            data-tll-supplement-close
            aria-label="Close Supplement Facts"
          >
            ×
          </button>
          <div class="tll-supplement-modal__image-wrap">
            <img class="tll-supplement-modal__image" alt="">
          </div>
        </div>
      `;

      document.body.appendChild(modal);
      return modal;
    };

    const openSupplementFactsModal = (url, alt = 'Supplement Facts') => {
      if (!url) return;

      const modal = ensureSupplementFactsModal();
      const image = modal.querySelector('.tll-supplement-modal__image');

      image.src = url;
      image.alt = alt;
      modal.removeAttribute('hidden');
      document.body.classList.add('tll-modal-open');
    };

    const closeSupplementFactsModal = () => {
      const modal = document.querySelector('[data-tll-supplement-modal]');
      if (!modal) return;

      modal.setAttribute('hidden', '');
      document.body.classList.remove('tll-modal-open');
    };

    const syncSubscribeButtonWidth = () => {
      const select = matchResult.querySelector('[data-tll-selling-plan]');
      const button = matchResult.querySelector('[data-tll-subscribe]');

      if (!select || !button) return;

      const selectWidth = Math.ceil(select.getBoundingClientRect().width);

      if (selectWidth > 0) {
        button.style.width = `${selectWidth}px`;
      }
    };

    const loadStampedBadge = (customReviewCount, attempt = 0) => {
      const badgeRoot = matchResult.querySelector('[data-tll-stamped-badge]');
      if (!badgeRoot) return;

      if (
        badgeRoot.dataset.tllStampedRequested !== 'true'
        && window.StampedFn
        && typeof window.StampedFn.loadBadges === 'function'
      ) {
        badgeRoot.dataset.tllStampedRequested = 'true';

        try {
          window.StampedFn.loadBadges();
        } catch (error) {
          console.warn('[Track Your Lot] Stamped badge load failed:', error);
        }
      }

      const badge = badgeRoot.querySelector('.stamped-badge');
      const caption = badgeRoot.querySelector('.stamped-badge-caption');

      if (caption && customReviewCount) {
        caption.innerHTML = `${escapeHtml(customReviewCount)}<span style="display:none;"> reviews</span>`;
        caption.setAttribute('data-reviews', customReviewCount);
        caption.setAttribute('aria-label', `${customReviewCount} reviews`);

        if (badge) {
          const rating = badge.getAttribute('data-rating');

          if (rating) {
            badge.setAttribute(
              'aria-label',
              `Rated ${rating} out of 5 stars ${customReviewCount} reviews`
            );
          }
        }
      }

      if ((!caption || !badgeRoot.querySelector('.stamped-badge-starrating')) && attempt < 40) {
        setTimeout(() => loadStampedBadge(customReviewCount, attempt + 1), 200);
      }
    };

    const renderRecordUnlocked = (record, productTitle, lotNumber, product = null) => {
      const file = getRecordFile(record);
      const testDate = getOptionalText(record, 'test_date');
      const thirdPartyLaboratory = getOptionalText(record, 'third_party_laboratory');
      const laboratoryLocation = getOptionalText(record, 'laboratory_location');
      const notes = getOptionalText(record, 'notes');
      const productTagLine = product?.productTagLine?.value
        ? String(product.productTagLine.value).trim()
        : selectedProductTagLine;
      const productImageUrl = product?.featuredImage?.url || selectedProductImage || '';
      const productImageAlt = product?.featuredImage?.altText || selectedProductImageAlt || productTitle;

      if (!file) {
        matchResult.innerHTML = '';
        setMessage('The lot number matched, but no test file is currently available.', 'warning');
        return;
      }

      let card = '';

      if (file.image?.url) {
        card = `
          <article class="tll-file-card">
            <div class="tll-file-card__heading">
              <div>
                <span class="tll-file-card__kicker">Test Result</span>
                <h3>Certificate of Analysis</h3>
              </div>
              <span class="tll-file-card__type">IMAGE</span>
            </div>
            <a class="tll-file-card__preview tll-file-card__preview--image" href="${escapeHtml(file.image.url)}" target="_blank" rel="noopener">
              <img
                src="${escapeHtml(file.image.url)}"
                alt="${escapeHtml(file.image.altText || `${productTitle} lot ${lotNumber} test result`)}"
                loading="lazy"
              >
            </a>
            <a class="tll-file-card__download" href="${escapeHtml(file.image.url)}" target="_blank" rel="noopener">
              <span class="tll-link-copy">Open test result</span> <span aria-hidden="true">↗</span>
            </a>
          </article>
        `;
      } else if (file.url) {
        const preview = file.previewImage?.url || '';
        const cleanFileUrl = String(file.url || '').split('?')[0].toLowerCase();
        const isPdf = Boolean(
          file.mimeType?.toLowerCase().includes('pdf')
          || cleanFileUrl.endsWith('.pdf')
        );
        const fileType = isPdf ? 'PDF' : 'FILE';

        card = `
          <article class="tll-file-card">
            <div class="tll-file-card__heading">
              <div>
                <span class="tll-file-card__kicker">Test Result</span>
                <h3>Certificate of Analysis</h3>
              </div>
              <span class="tll-file-card__type">${escapeHtml(fileType)}</span>
            </div>

            ${isPdf ? `
              <button
                type="button"
                class="tll-file-card__preview tll-file-card__preview--pdf tll-file-card__preview-button"
                data-tll-open-pdf="${escapeHtml(file.url)}"
                aria-label="Open Certificate of Analysis"
              >
                <iframe
                  class="tll-file-card__pdf-frame"
                  src="${escapeHtml(file.url)}#page=1&zoom=page-width&toolbar=0&navpanes=0"
                  title="Certificate of Analysis preview"
                  loading="lazy"
                  tabindex="-1"
                  aria-hidden="true"
                ></iframe>
                <span class="tll-file-card__preview-overlay">Click to enlarge</span>
              </button>

              <button
                type="button"
                class="tll-file-card__download tll-file-card__download-button"
                data-tll-open-pdf="${escapeHtml(file.url)}"
              >
                <span class="tll-link-copy">View test result</span> <span aria-hidden="true">↗</span>
              </button>
            ` : `
              <a class="tll-file-card__preview tll-file-card__preview--file" href="${escapeHtml(file.url)}" target="_blank" rel="noopener">
                ${preview
                  ? `<img src="${escapeHtml(preview)}" alt="Certificate preview" loading="lazy">`
                  : `<div class="tll-pdf-placeholder">${escapeHtml(fileType)}</div>`
                }
              </a>
              <a class="tll-file-card__download" href="${escapeHtml(file.url)}" target="_blank" rel="noopener">
                <span class="tll-link-copy">Open test result</span> <span aria-hidden="true">↗</span>
              </a>
            `}
          </article>
        `;
      }

      if (!card) {
        matchResult.innerHTML = '';
        setMessage('The lot number matched, but no test file is currently available.', 'warning');
        return;
      }

      const coaUrl = file.image?.url || file.url || '';
      const savedAt = Date.now();

      saveHistoryEntry({
        productTitle,
        productTagLine,
        productImage: productImageUrl,
        productUrl: selectedProductUrl,
        lotNumber,
        coaUrl,
        savedAt,
        savedIso: new Date(savedAt).toISOString(),
        displayDate: new Intl.DateTimeFormat(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }).format(savedAt)
      });

      const metadata = [
        `<p>Lot <strong>${escapeHtml(lotNumber)}</strong></p>`,
        testDate ? `<p><strong>Test date:</strong> ${escapeHtml(formatDate(testDate))}</p>` : '',
        thirdPartyLaboratory ? `<p><strong>Third-Party Laboratory:</strong> ${escapeHtml(thirdPartyLaboratory)}</p>` : '',
        laboratoryLocation ? `<p><strong>Laboratory Location:</strong> ${escapeHtml(laboratoryLocation)}</p>` : '',
        notes ? `<div class="tll-result-section__notes">${escapeHtml(notes).replace(/\n/g, '<br>')}</div>` : ''
      ].filter(Boolean).join('');

      matchResult.innerHTML = `
        <section class="tll-match-card">
          <div class="tll-match-card__media">
            ${productImageUrl
              ? `<img src="${escapeHtml(productImageUrl)}" alt="${escapeHtml(productImageAlt)}" loading="lazy">`
              : '<div class="tll-match-card__image-placeholder" aria-hidden="true"></div>'
            }
          </div>
          <div class="tll-match-card__content">
            <span class="tll-result-section__eyebrow">MATCH FOUND</span>
            <h2>${escapeHtml(productTitle)}</h2>
            ${productTagLine ? `<p class="tll-match-card__tagline">${escapeHtml(productTagLine)}</p>` : ''}
            ${selectedProductUrl
              ? `<a class="tll-match-card__view-product" href="${escapeHtml(selectedProductUrl)}"><span class="tll-link-copy">View Product</span> <span aria-hidden="true">→</span></a>`
              : ''
            }
            <div class="tll-match-card__metadata">${metadata}</div>
          </div>
        </section>
      `;

      result.innerHTML = `
        <div class="tll-file-grid tll-file-grid--single">${card}</div>
      `;

      expandResult(matchResult);
      expandResult(result, 90);

    };

    const trackLot = async () => {
      if (!selectedProductId) {
        setMessage('Please select a product first.', 'error');
        return;
      }

      const rawLot = lotInput.value.trim();

      if (!rawLot) {
        setMessage('Please enter a lot number.', 'error');
        lotInput.focus();
        return;
      }

      setLoading(true);
      matchResult.innerHTML = '';
      result.innerHTML = '';

      try {
        const product = await fetchProduct(selectedProductId);

        if (!product) {
          throw new Error('Product not found.');
        }

        let resolvedProductTagLine = product?.productTagLine?.value
          ? String(product.productTagLine.value).trim()
          : selectedProductTagLine;

        if (!resolvedProductTagLine) {
          resolvedProductTagLine = await fetchProductTagLine(
            product.handle || selectedProductHandle
          );
        }

        if (resolvedProductTagLine) {
          selectedProductTagLine = resolvedProductTagLine;
          product.productTagLine = { value: resolvedProductTagLine };
        }

        if (!selectedProductImage && product.featuredImage?.url) {
          selectedProductImage = product.featuredImage.url;
          selectedProductImageAlt = product.featuredImage.altText || product.title;
          updateSelectedProductImage(selectedProductImage, selectedProductImageAlt);
        }

        const records = product.metafield?.references?.nodes || [];

        if (!records.length) {
          setMessage('No COA records are currently assigned to this product.', 'warning');
          return;
        }

        const enteredLots = rawLot
          .split(',')
          .map(value => value.trim().toLowerCase())
          .filter(Boolean);

        let matchedRecord = null;
        let matchedLot = '';

        for (const record of records) {
          const recordLots = getRecordLots(record);
          const match = enteredLots.find(lot => recordLots.includes(lot));

          if (match) {
            matchedRecord = record;
            matchedLot = match;
            break;
          }
        }

        if (!matchedRecord) {
          setMessage('The lot number you entered was not found for this product.', 'error');
          return;
        }

        renderRecordUnlocked(matchedRecord, product.title, matchedLot || rawLot, product);
      } catch (error) {
        console.error('[Track Your Lot] Lookup error:', error);
        setMessage('There was an error retrieving this product’s test information. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    productInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(searchProducts, 180);
    });

    productInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeResults();
    });

    resultsBox.addEventListener('click', (event) => {
      const item = event.target.closest('[data-product-id]');
      if (!item) return;

      selectProduct(
        item.dataset.productId,
        item.dataset.productTitle,
        item.dataset.productImage || '',
        item.dataset.productImageAlt || '',
        item.dataset.productUrl || '',
        item.dataset.productHandle || '',
        item.dataset.productTagline || ''
      );
    });

    changeProduct.addEventListener('click', () => {
      selectedProductId = null;
      selectedProductTitle = '';
      selectedProductImage = '';
      selectedProductImageAlt = '';
      selectedProductUrl = '';
      selectedProductHandle = '';
      selectedProductTagLine = '';
      if (selectedProductLink) {
        selectedProductLink.href = '#';
        selectedProductLink.hidden = true;
      }

      selectedBox.hidden = true;
      updateSelectedProductImage('', '');
      lotField.hidden = true;
      lotInput.value = '';
      matchResult.innerHTML = '';
      result.innerHTML = '';

      const productField = productInput.closest('.tll-field--product');
      productField.hidden = false;
      productInput.value = '';
      productInput.focus();
    });

    searchButton.addEventListener('click', trackLot);

    lotInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        trackLot();
      }
    });

    result.addEventListener('click', (event) => {
      const pdfButton = event.target.closest('[data-tll-open-pdf]');
      if (!pdfButton) return;

      event.preventDefault();
      openPdfModal(pdfButton.getAttribute('data-tll-open-pdf'), 'Certificate of Analysis');
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-tll-pdf-close]')) {
        closePdfModal();
      }

    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closePdfModal();
      }
    });

    if (historyClear) {
      historyClear.addEventListener('click', clearHistory);
    }

    if (historyList) {
      historyList.addEventListener('click', (event) => {
        const button = event.target.closest('[data-history-open]');
        if (!button) return;

        const url = button.getAttribute('data-history-open');
        if (url) openPdfModal(url, 'Certificate of Analysis');
      });
    }

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeResults();
    });

    renderHistory();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-track-lot-root]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
